import { useMemo, type ReactElement } from 'react'

import { AvatarPessoa } from '@/components/avatar-pessoa'
import { Card, CardContent, SelectSeparator, Skeleton } from '@/components/ui'
import { formatarProfissaoLocalizacao } from '@/features/clientes/utils/profissao-e-localizacao-cliente'
import { cn } from '@/infra'
import type { TClienteSimplificado } from '@/types/cliente'
import { StringUtils } from '@/utils'

const ClienteCardSkeleton = () => {
  return (
    <Card className="shadow-cz relative w-full border p-4" data-testid="card-cliente-skeleton">
      <div className="mb-4 flex items-start justify-between">
        <Skeleton className="h-5 w-24" />
        <Skeleton className="h-8 w-32" />
      </div>
      <SelectSeparator className="h-full" />

      <CardContent className="border-muted grid grid-cols-2 gap-4 border-t-1 px-0 py-4">
        <div className="border-muted flex gap-4 border-r-1 pr-4">
          <Skeleton className="h-12 w-12 rounded-full" />
          <div className="flex-1 space-y-2 overflow-hidden">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        </div>

        <div className="flex-1 flex-col justify-start space-y-2">
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-4 w-1/3" />
        </div>
      </CardContent>
    </Card>
  )
}
type TClienteCardProps = {
  cliente: TClienteSimplificado
  action?: (cliente: TClienteSimplificado) => ReactElement
}

const ClienteCard = ({ cliente, action }: TClienteCardProps) => {
  const tipoDoClienteFormatado = useMemo(() => {
    if (cliente.tipoDoCliente === 'LeadRepresado') return 'Lead Represado'
    return cliente.tipoDoCliente
  }, [cliente.tipoDoCliente])

  return (
    <Card className="shadow-cz relative w-full border p-4" data-testid="card-cliente">
      <div className="mb-4 flex items-center justify-between sm:items-start">
        <div className={cn('text-emerald-800 uppercase', { visible: !cliente.tipoDoCliente })}>
          {tipoDoClienteFormatado}
        </div>
        {action?.(cliente)}
      </div>
      <SelectSeparator className="w-full sm:h-full" />
      <CardContent className="border-muted flex flex-col gap-4 py-4 sm:grid sm:grid-cols-2 sm:gap-4 sm:border-t-1 sm:px-0">
        <div className="border-muted flex gap-4 pr-4 sm:border-r-1">
          <AvatarPessoa nome={cliente.nome} imagem={cliente.imagemDaPessoa} />
          <div className="flex-1 space-y-1 overflow-hidden">
            <h3 className="line-clamp-2 text-base font-normal sm:font-medium">
              {StringUtils.capitalize(cliente.nome)}
            </h3>
            <p className="text-muted-foreground text-sm">
              {formatarProfissaoLocalizacao(cliente.profissao, cliente?.endereco)}
            </p>
          </div>
        </div>
        <div className="flex-1 flex-col justify-start space-y-1">
          <p className="text-sm">{cliente.telefonePrincipal}</p>
          <p className="text-muted-foreground text-sm">{cliente.cpf}</p>
        </div>
      </CardContent>
    </Card>
  )
}

export { ClienteCard, ClienteCardSkeleton }
